import { ApolloError, useQuery } from '@apollo/client';
import React, { useMemo, useContext, FunctionComponent, memo } from 'react';
import { LoginStateContext } from '../../Features/Login/Contexts/LoginStateContext/LoginStateContext';
import {
  System,
  useBaseRouteValue,
} from '../../Features/Routing/Utils/routerUtils';
import {
  ActiveDatabaseInfoQuery,
  ActiveDatabaseInfoQueryVariables,
  ActiveDatabaseInfoQuery_me_sessionInfo as ISessionInfo,
} from './_QueryTypes/ActiveDatabaseInfoQuery';
import ActiveDatabaseInfo from './ActiveDatabaseInfo.gql';
import { IDelsystem } from '../../Features/Delsystem/_Common/Const/Delsystem';
import { IRutinInfo } from '../../Types/RutinInfoType';
import { LoginStateQuery_me_databases } from '../../Features/Login/Contexts/LoginStateContext/Queries/_QueryTypes/LoginStateQuery';
import { createDatabaseContextValue } from './ActiveDatabaseContextUtils';
export type { ISessionInfo };

export interface IAvailableRoutine extends IRutinInfo {
  readonly: boolean;
}

export interface IRoutineAvailability {
  [rutinId: number]: IAvailableRoutine | undefined;
}
interface IActiveDatabaseProviderProps {
  activeDatabase?: string | null;
  system?: System | null;
}
export interface IActiveDatabaseContext {
  activeDatabase: string | null;
  activeDatabaseInfo?: ActiveDatabaseInfoQuery;
  activeDatabaseInfoLoading: boolean;
  activeDatabaseInfoError: ApolloError | undefined;
  availableDelsystem?: IDelsystem[];
  availableRoutines?: IRoutineAvailability;
  system: System | null;
  userID: string | null;
}

const defaultValues: IActiveDatabaseContext = {
  activeDatabase: null,
  activeDatabaseInfoLoading: true,
  activeDatabaseInfoError: undefined,
  system: null,
  userID: null,
};

export const ActiveDatabaseContext: React.Context<IActiveDatabaseContext> =
  React.createContext(defaultValues);

export const ActiveDatabaseProvider: React.FC<
  React.PropsWithChildren<IActiveDatabaseProviderProps>
> = props => {
  const { databases, isLoggedIn } = useContext(LoginStateContext);
  const { system, database } = useBaseRouteValue();
  return (
    <InnerActiveDatabaseProvider
      databases={databases}
      system={system}
      database={isLoggedIn ? database : undefined}
      {...props}
    />
  );
};

interface IInnerActiveDatabaseProviderProps
  extends IActiveDatabaseProviderProps {
  databases: LoginStateQuery_me_databases[];
  database?: string;
}
export const InnerActiveDatabaseProvider: FunctionComponent<
  React.PropsWithChildren<IInnerActiveDatabaseProviderProps>
> = memo(props => {
  const {
    activeDatabase: activeDatabaseProp,
    system: systemProp,
    // ...rest
  } = props;
  // Om databasen man är inloggad i har annan case än den angivna så rättar vi upp det
  // så contexten innehåller databasnamnet med rätt case
  const activeDatabase: string | null =
    props.databases.find(
      db =>
        db.name.toUpperCase() ===
        (activeDatabaseProp ?? props.database)?.toUpperCase()
    )?.name ??
    activeDatabaseProp ??
    props.database ??
    null;
  const system: System = systemProp ?? props.system ?? System.Lokalt;
  const skipLoadInfo = activeDatabase == null;
  const { data, loading, error } = useQuery<
    ActiveDatabaseInfoQuery,
    ActiveDatabaseInfoQueryVariables
  >(ActiveDatabaseInfo, {
    variables: { dbName: activeDatabase ?? '' },
    skip: skipLoadInfo,
  });

  const userID = (data as ActiveDatabaseInfoQuery)?.me?.[0]?.userId ?? null;

  const value = useMemo<IActiveDatabaseContext>(
    () =>
      createDatabaseContextValue(
        activeDatabase,
        data,
        loading,
        error,
        skipLoadInfo,
        system,
        userID
      ),
    [activeDatabase, data, loading, error, skipLoadInfo, system, userID]
  );

  return (
    <ActiveDatabaseContext.Provider value={value}>
      {props.children}
    </ActiveDatabaseContext.Provider>
  );
});
export const ActiveDatabaseConsumer = ActiveDatabaseContext.Consumer;

export const TestDBProvider: React.FunctionComponent<
  React.PropsWithChildren<{}>
> = props => {
  return (
    <ActiveDatabaseProvider activeDatabase={'TestDB'}>
      {props.children}
    </ActiveDatabaseProvider>
  );
};
